/* eslint-disable no-console */ import axiosStatic, { AxiosHeaders } from 'axios';
import { map, shareReplay, tap } from 'rxjs';
import { handlePolicyConflictError } from 'api/project-policy/error-handler';
import { getCurrentApiHost, API_NAMESPACE, API_VERSION } from 'config/environment';
// import { toastService } from "components/molecules/toast";
import { ApiKey, logger, CustomerPreview } from 'services';
import { logout } from 'utils/logout';
import { addHopIdToHeader } from 'utils/window-utils';
const EXCLUDE_LOGOUT_PATHS = [
    '/setup-profile',
    '/login',
    '/2fa'
];
/**
 * The public web-form route (`forms/:customerName/:instanceHash`, see
 * non-protected-routes.tsx) is served to anonymous visitors. A User/Member
 * picker field on the form fires an authenticated GET /users/search that 401s
 * for logged-out users — that 401 must NOT log them out / redirect to /login.
 *
 * Only the two-segment public pattern (`/forms/<slug>/<hash>`) is excluded;
 * the authenticated form-template editor routes (`/forms/new`,
 * `/forms/:templateId`) have a single segment after `/forms` and must keep
 * logging out normally. Mirrors the public-form detection in
 * mobile-view-overlay.tsx.
 */ const isPublicWebFormPath = (pathname)=>{
    const segments = pathname.split('/');
    return segments[1] === 'forms' && segments[2] !== undefined && segments[2] !== '' && segments[3] !== undefined && segments[3] !== '';
};
const shouldSkipLogout = (pathname)=>EXCLUDE_LOGOUT_PATHS.includes(pathname) || isPublicWebFormPath(pathname);
/** Wire name for the initiator header (hyphenated; not a valid TS object literal key for naming rules). */ const INITIATOR_SOURCE_HEADER = 'initiator-source';
// Create config function that uses current API host dynamically
const getConfig = ()=>({
        baseURL: `${getCurrentApiHost()}/${API_NAMESPACE}/${API_VERSION}`,
        adapter: 'fetch',
        headers: new AxiosHeaders()
    });
const withCustomHeader = (header, configuration)=>{
    const configToUse = configuration || getConfig();
    return {
        ...configToUse,
        headers: configToUse.headers.set(header)
    };
};
const isMethodAllowedInPreview = (method = '', url = '')=>/(get)/i.test(method) || /(post)/i.test(method) && (url.includes('portal') || url.includes('resolve-widgets') || url.includes('dashboard') || url.includes('fields/searchLight') || url.includes('tasks/searchlightV1') || url.includes('pivot') || url.includes('localhost:12345') || url.includes('kanbanLayout') || url.includes('kanbanV1ForProjectBoard') || url.includes('kanbanV1ColForProjectBoard') || url.includes('batch') || url.includes('entities') || url.includes('run') && url.includes('server-action'));
const injectApiKey = (configuration)=>{
    return Promise.resolve(withCustomHeader(ApiKey.header(), configuration));
};
const injectPreviewApiKey = async (configuration)=>{
    return Promise.resolve(withCustomHeader(CustomerPreview.header(), configuration));
};
const removeCookieIfPreview = async (configuration)=>{
    const [isPreviewMode] = await CustomerPreview.isPreviewMode();
    if (!isPreviewMode) {
        return Promise.resolve(configuration);
    }
    return Promise.resolve({
        ...configuration,
        fetchOptions: {
            credentials: 'omit'
        }
    });
};
const injectHopId = (configuration)=>Promise.resolve(withCustomHeader(addHopIdToHeader(), configuration));
const injectCommonRequestHeaders = (configuration)=>{
    let config = configuration;
    config = withCustomHeader({
        [INITIATOR_SOURCE_HEADER]: 'UI'
    }, config);
    return Promise.resolve(config);
};
// Interceptor to ensure baseURL always uses current API host (updated after cookiesEnabled check)
const updateBaseUrlToCurrentHost = (configuration)=>{
    const currentHost = getCurrentApiHost();
    // Update the host while preserving the path (e.g. /api/v2 stays /api/v2)
    if (configuration.baseURL) {
        const updatedBaseURL = configuration.baseURL.replace(/^https?:\/\/[^/]+/, currentHost);
        return Promise.resolve({
            ...configuration,
            baseURL: updatedBaseURL
        });
    }
    return Promise.resolve({
        ...configuration,
        baseURL: `${currentHost}/${API_NAMESPACE}/${API_VERSION}`
    });
};
const rejectWriteRequestsInPreview = async (configuration)=>{
    if (isMethodAllowedInPreview(configuration === null || configuration === void 0 ? void 0 : configuration.method, configuration === null || configuration === void 0 ? void 0 : configuration.url)) {
        return Promise.resolve(configuration);
    }
    const [preview] = await CustomerPreview.isPreviewMode();
    if (preview) {
        throw new Error('editing disallowed in preview mode');
    }
    return Promise.resolve(configuration);
};
var ApiErrorCode = /*#__PURE__*/ function(ApiErrorCode) {
    ApiErrorCode["InvalidKey"] = "INVALID_KEY";
    ApiErrorCode["NotAuthorized"] = "NOT_AUTHORIZED";
    return ApiErrorCode;
}(ApiErrorCode || {});
export var ApiStatusCode = /*#__PURE__*/ function(ApiStatusCode) {
    ApiStatusCode[ApiStatusCode["NotAuthorized"] = 401] = "NotAuthorized";
    ApiStatusCode[ApiStatusCode["PolicyConflict"] = 422] = "PolicyConflict";
    ApiStatusCode[ApiStatusCode["TooManyRequests"] = 429] = "TooManyRequests";
    return ApiStatusCode;
}({});
/**
 * Dispatches a failed-request log to the right severity bucket.
 *
 * Datadog's `service:rocket-frontend status:error` filter is meant for
 * actionable failures (real backend bugs, network outages). Routine 4xx
 * responses (401 token-expired → logout, 403 permission-denied → no-access
 * page, 429 rate-limit → FE retries, 400 validation → surfaced in the form)
 * are expected app states, not crashes. Logging them at `error` severity
 * floods the error stream and makes real crashes invisible.
 *
 * Mapping:
 *   - 5xx                      → `logger.error` (real backend failure)
 *   - 4xx                      → `logger.warn`  (expected app state)
 *   - 0 / no response (network) → `logger.error` (real network failure)
 *
 * The wording of the log message is unchanged so existing Datadog queries
 * that match on `Request failed with status code …` keep working — only
 * the `status` facet moves.
 */ export const logAxiosFailure = (error)=>{
    var _error_response, _error_config;
    const status = (_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status;
    const message = error.message || 'Request failed';
    const context = {
        error,
        status,
        url: (_error_config = error.config) === null || _error_config === void 0 ? void 0 : _error_config.url
    };
    if (status === undefined || status === 0) {
        // Network error / request never made it to the server — still actionable.
        logger.error(message, context);
        return;
    }
    if (status >= 500) {
        logger.error(message, context);
        return;
    }
    // 4xx — demoted to warn. Still queryable via `status:warn`.
    logger.warn(message, context);
};
export const handleAxiosError = (error)=>{
    var _error_response, _error_response_data_errors_, _error_response_data_errors, _error_response_data, _error_response1, _error_response_data_, _error_response_data1, _error_response2, _error_response3, _error_response4;
    logAxiosFailure(error);
    if (((_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === 401 && (((_error_response1 = error.response) === null || _error_response1 === void 0 ? void 0 : (_error_response_data = _error_response1.data) === null || _error_response_data === void 0 ? void 0 : (_error_response_data_errors = _error_response_data.errors) === null || _error_response_data_errors === void 0 ? void 0 : (_error_response_data_errors_ = _error_response_data_errors[0]) === null || _error_response_data_errors_ === void 0 ? void 0 : _error_response_data_errors_.errorCode) === "NOT_AUTHORIZED" || ((_error_response2 = error.response) === null || _error_response2 === void 0 ? void 0 : (_error_response_data1 = _error_response2.data) === null || _error_response_data1 === void 0 ? void 0 : (_error_response_data_ = _error_response_data1[0]) === null || _error_response_data_ === void 0 ? void 0 : _error_response_data_.errorCode) === "NOT_AUTHORIZED")) {
        if (!shouldSkipLogout(window.location.pathname)) {
            logout();
        }
    } else if (((_error_response3 = error.response) === null || _error_response3 === void 0 ? void 0 : _error_response3.status) === 422) {
        handlePolicyConflictError(error);
    } else if (((_error_response4 = error.response) === null || _error_response4 === void 0 ? void 0 : _error_response4.status) === 429) {
        var _error_response5;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const retryAt = (_error_response5 = error.response) === null || _error_response5 === void 0 ? void 0 : _error_response5.headers['x-retry-after'];
    // * Commenting this because of circular dependency
    // toastService({
    //   title: "Too many requests!",
    //   message: `You have exceeded the request limit.${
    //     retryAt ? ` Please try again in ${format("m", retryAt)}.` : ""
    //   }`,
    //   duration: 10,
    // }).error();
    }
    return Promise.reject(error);
};
const attachAxiosInterceptors = (client)=>{
    client.interceptors.request.use(updateBaseUrlToCurrentHost);
    client.interceptors.request.use(injectPreviewApiKey);
    client.interceptors.request.use(injectApiKey);
    client.interceptors.request.use(removeCookieIfPreview);
    client.interceptors.request.use(injectHopId);
    client.interceptors.request.use(injectCommonRequestHeaders);
    client.interceptors.request.use(rejectWriteRequestsInPreview);
    client.interceptors.response.use((response)=>response, handleAxiosError);
};
export const axios = ()=>ApiKey.tokens().pipe(map(()=>axiosStatic.create(withCustomHeader(ApiKey.header(), getConfig()))), tap(attachAxiosInterceptors), shareReplay(1));
